import { useEffect, useState } from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { AlarmClock, TriangleAlert, Wifi, WifiOff } from "lucide-react"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { Button } from "@/components/ui/button"
import { MainContainer } from "@/components/ui/container"
import { useLocationRealtime } from "@/hooks/use-location-realtime"
import { formatDateTime } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * The kitchen and bar station screen.
 *
 * **This page is a deliberate exception to the design system's two-height
 * scale**, and it is the only one. Every other surface is authored for a hand at
 * arm's length; this one is read from a metre away by someone who is not going
 * to lean in. `h-11 md:h-9` at that distance is a target you miss with the back
 * of a knuckle, and `text-sm` is a line you squint at. So the type steps up and
 * the tap targets grow, and nothing here is copied to another screen.
 *
 * What is *not* excepted: the dot rule, tabular numerals, status tokens, and
 * WCAG AA. Urgency is carried by an icon and a word as well as a colour,
 * because a kitchen is exactly where a colour-blind reader and a glare-washed
 * screen both show up.
 */

type Ticket = {
  key: string
  tableName: string | null
  serviceType: string
  elapsedMinutes: number
  urgency: "on_time" | "warning" | "late"
  orders: {
    id: number
    name: string | null
    quantity: number
    notes: string | null
    status: string
    modifiers: { name?: string }[]
  }[]
}

type SharedCurrentLocation = {
  id: number
  name: string
} | null

type Props = {
  station: string
  stations: { value: string; label: string }[]
  tickets: Ticket[]
  currentLocation?: SharedCurrentLocation
}

const tKds = (key: string) => t(`ui.react.pages.staff.location.kds.show.${key}`)

// Where a ticket goes when the station taps it. `ready` is the end of the
// station's responsibility; what happens after belongs to the floor.
const NEXT_STATUS: Record<string, string | null> = {
  pending: "in_progress",
  in_progress: "ready",
  ready: null,
}

const PREVIOUS_STATUS: Record<string, string | null> = {
  pending: null,
  in_progress: "pending",
  ready: "in_progress",
}

// A heartbeat for the CLOCK, not for connectivity -- and it stays even though
// this board is now on the location's realtime stream.
//
// `elapsedMinutes` and `urgency` are computed by `Staff::StationBoardQuery` on
// the server, deliberately, so that two cards can never disagree about how late
// a ticket is. The cost of that choice is that a ticket nobody touches produces
// no event: under realtime alone its timer would freeze at whatever it read
// when the board last loaded, and a ticket would sail past `late_after` without
// the screen ever saying so. The one thing this display exists to shout about
// is exactly the thing that would go silent.
//
// So the board still asks on a timer. This is not the degraded-mode poll --
// `useLocationRealtime` runs its own, faster, only while the socket is down.
const CLOCK_REFRESH_MS = 15_000

function UrgencyMark({ urgency }: { urgency: Ticket["urgency"] }) {
  if (urgency === "on_time") return null

  const late = urgency === "late"
  const Icon = late ? TriangleAlert : AlarmClock

  return (
    <span
      className={`flex items-center gap-2 text-2xl font-semibold ${late ? "text-destructive" : "text-warning"}`}
      data-testid={`ticket-urgency-${urgency}`}
    >
      {/* Icon plus word plus colour. Any one of the three carries the meaning
          on its own, which is what keeps this readable through glare and
          without colour vision. */}
      <Icon className="size-7 shrink-0" aria-hidden="true" />
      {late ? tKds("late") : tKds("warning")}
    </span>
  )
}

function TicketCard({ ticket, station }: { ticket: Ticket; station: string }) {
  const status = ticket.orders[0]?.status ?? "pending"
  const next = NEXT_STATUS[status]
  const previous = PREVIOUS_STATUS[status]

  // `/kds/orders/:id`, not `/manage/kds/orders/:id`. The station board is
  // deliberately NOT under /manage -- /manage is manager-only and this is a
  // wall-mounted screen a cook taps -- and the route has always been
  // `staff_location_kds_advance_order`, at `/kds/orders/:id`. The `/manage`
  // prefix here matched nothing, so every tap on this board raised a routing
  // error instead of moving the ticket, from the day the screen shipped until
  // tests/staff/inventory.spec.ts drove it.
  const move = (to: string) => {
    router.patch(
      `/kds/orders/${ticket.orders[0]?.id}`,
      { status: to, station },
      { preserveScroll: true }
    )
  }

  return (
    <article
      className="flex flex-col gap-4 rounded-lg border-2 border-border bg-card p-5"
      data-testid={`ticket-${ticket.key}`}
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-3xl font-bold tracking-tight">
            {ticket.tableName ?? tKds("no_table")}
          </p>
          <p className="text-xl tabular-nums text-muted-foreground" data-testid="ticket-elapsed">
            {tKds("minutes_waiting").replace("%{count}", String(ticket.elapsedMinutes))}
          </p>
        </div>
        <UrgencyMark urgency={ticket.urgency} />
      </header>

      <ul className="space-y-2">
        {ticket.orders.map((order) => (
          <li key={order.id} className="flex gap-3 text-2xl">
            <span className="min-w-10 font-bold tabular-nums">{order.quantity}×</span>
            <span className="min-w-0">
              <span className="font-medium">{order.name}</span>
              {order.notes ? (
                <span className="block text-xl text-muted-foreground">{order.notes}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>

      {/* One tap forward, one tap back. No confirmation dialog: on a greasy
          screen a metre away it costs more than the mistake it prevents, and
          the mistake is undone by the control sitting next to it. */}
      <footer className="flex gap-3">
        {next ? (
          <Button
            className="h-16 flex-1 text-xl"
            onClick={() => move(next)}
            data-testid={`ticket-advance-${ticket.key}`}
          >
            {tKds(`advance_to_${next}`)}
          </Button>
        ) : (
          <p className="flex h-16 flex-1 items-center justify-center text-xl font-semibold text-success">
            {tKds("ready")}
          </p>
        )}
        {previous ? (
          <Button
            variant="outline"
            className="h-16 px-6 text-xl"
            onClick={() => move(previous)}
            data-testid={`ticket-undo-${ticket.key}`}
          >
            {tKds("undo")}
          </Button>
        ) : null}
      </footer>
    </article>
  )
}

/**
 * Whether the board is still hearing from the server, and when it last did.
 *
 * A wall display has nobody watching it for staleness, so it declares its own —
 * and the two halves answer different questions. The mode says whether news
 * arrives the instant it happens or on a timer; the timestamp says how old what
 * you are reading actually is. A board that lost its socket ten minutes ago and
 * one that is perfectly current used to render identically.
 *
 * Meaning never rests on the colour. Icon, word, weight and border each carry it
 * alone, which is what keeps the degraded state readable through kitchen glare
 * and without colour vision — the same standard `UrgencyMark` is held to.
 */
function FreshnessLine({
  connectionMode,
  refreshedAt,
}: {
  connectionMode: "realtime" | "polling"
  refreshedAt: number
}) {
  const live = connectionMode === "realtime"
  const Icon = live ? Wifi : WifiOff

  return (
    <div className="flex justify-center py-4">
      <p
        // `aria-live` so the drop is announced rather than only drawn. It is
        // polite, not assertive: losing the socket is worth saying once, not
        // worth interrupting someone mid-ticket.
        aria-live="polite"
        className={
          live
            ? "flex items-center gap-2 text-base tabular-nums text-muted-foreground"
            : "flex items-center gap-2 rounded-md border border-warning/40 px-3 py-1 text-base font-semibold tabular-nums text-warning"
        }
        data-testid="kds-freshness"
        // The mode as data, so a browser test can assert on the state rather
        // than on localized copy.
        data-connection={connectionMode}
      >
        <Icon className="size-5 shrink-0" aria-hidden="true" />
        <span>{live ? tKds("live") : tKds("degraded")}</span>
        <span aria-hidden="true">·</span>
        {/* No time zone passed, on purpose: this is the browser's own
            `Date.now()`, and the browser is the screen on the kitchen wall. */}
        <span>
          {`${tKds("refreshed")} ${formatDateTime(new Date(refreshedAt), {
            options: { timeStyle: "short" },
          })}`}
        </span>
      </p>
    </div>
  )
}

export default function StaffLocationKdsShow() {
  const { station, stations, tickets, currentLocation } = usePage<Props>().props
  // Seeded rather than left null: `tickets` is already in props on this first
  // paint, so "just now" is the truth, and a null would render the line once
  // without its clock and then shift it in a frame later.
  const [refreshedAt, setRefreshedAt] = useState<number>(() => Date.now())

  // The station board is deliberately absent from `staff-layout`'s
  // `operationalRealtimeEnabled` list, so the layout holds no subscription for
  // this path and the page owns its own. One socket either way, and the
  // wall-display behaviour stays local to the wall display instead of becoming
  // a branch every other staff screen carries.
  const { connectionMode } = useLocationRealtime({
    enabled: Boolean(currentLocation?.id),
    locationId: currentLocation?.id,
    path: "/kds",
  })

  // Stamped from the arrival of board data itself rather than from any one
  // fetch's callback. Three different things reload this board -- the clock
  // below, a realtime event, and the degraded-mode poll inside the hook -- and
  // the line claims "last heard from the server", which is true of all three.
  useEffect(() => {
    setRefreshedAt(Date.now())
  }, [tickets])

  useEffect(() => {
    const timer = window.setInterval(() => {
      router.reload({ only: ["tickets"] })
    }, CLOCK_REFRESH_MS)

    return () => window.clearInterval(timer)
  }, [])

  return (
    <>
      <Head title={tKds("title")} />

      {/* The one staff surface that takes the whole viewport, and the reason is
          physical rather than stylistic: this is bolted to a kitchen wall and
          read from a metre away. Capping it at `md` (1280px) would leave most of
          a wall-mounted screen empty while shrinking the tickets the exception
          at the top of this file exists to enlarge. */}
      <MainContainer size="fullWidth">
        <div className="flex flex-wrap items-center justify-between gap-3 py-4">
          <h1 className="text-3xl font-bold tracking-tight">{tKds("title")}</h1>
          <nav className="flex flex-wrap gap-2" aria-label={tKds("station")}>
            {stations.map((option) => (
              <Button
                key={option.value}
                variant={option.value === station ? "default" : "outline"}
                className="h-14 px-5 text-lg"
                onClick={() => router.get("/kds", { station: option.value })}
                data-testid={`station-${option.value}`}
              >
                {option.label}
              </Button>
            ))}
          </nav>
        </div>

        {tickets.length === 0 ? (
          <p className="py-16 text-center text-2xl text-muted-foreground" data-testid="kds-empty">
            {tKds("empty")}
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="kds-board">
            {tickets.map((ticket) => (
              <TicketCard key={ticket.key} ticket={ticket} station={station} />
            ))}
          </div>
        )}

        <FreshnessLine connectionMode={connectionMode} refreshedAt={refreshedAt} />
      </MainContainer>
    </>
  )
}

StaffLocationKdsShow.layout = (page: React.ReactNode) => <StaffLayout>{page}</StaffLayout>
